import React, { useEffect } from "react";
import { BrowserRouter, useLocation } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { ThemeProvider } from "styled-components";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import { GlobalStyles } from "./styles/GlobalStyles";
import { AppRoutes } from "./routes";
import { useAuth } from "./hooks/useAuth";
import { initializeLanguage } from "./store/slices/languageSlice";
import { syncService } from "./services/syncService";
import { RootState } from "./store";
import { initWebVitals } from './utils/webVitals';
import { SEO } from './components/SEO';
import ScrollToTop from './components/ScrollToTop';
import { ErrorMessage } from './components/common/ErrorMessage';
import { trackPageView } from './services/analytics';
import './i18n';
import i18n from './i18n';
import './styles/mobile-optimizations.css';
import { theme } from './styles/theme';
import { DefaultTheme } from 'styled-components';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { watchlistService } from './services/watchlistService';

import { ErrorBoundary } from './components/common/ErrorBoundary';
import { SocialBar } from './components/ads/SocialBar';
import { ScrollTopButton } from './components/common/ScrollTopButton';
import AdSafetyGuard from './components/ads/AdSafetyGuard';
import Crxcr2CamsWidget from './components/ads/Crxcr2CamsWidget';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

interface AppContentProps {}

const AppContent = ({}: AppContentProps) => {
  console.warn("AppContent: rendu");
  const location = useLocation();
  const dispatch = useDispatch();
  const { initialize: initializeAuth } = useAuth();
  const user = useSelector((state: RootState) => state.user, shallowEqual);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);

  useEffect(() => {
    // Initialize auth
    initializeAuth();

    // Initialize sync service
    syncService.initialize();

    // Initialize watchlist service if user is logged in
    if (user?.uid) {
      watchlistService.initialize();
    }

    // Report web vitals
    initWebVitals();

    // Cleanup
    return () => {
      syncService.stop();
      watchlistService.stop();
    };
  }, [initializeAuth, user?.uid]);



  
  return (
    <>
      <AdSafetyGuard />
      <GlobalStyles theme={theme} />
      <SEO title="Cinédelux" description="Découvrez les derniers films et séries" />
      <ScrollToTop />
      <AppRoutes />
      {/* Bouton retour en haut pour mobile */}
      <ScrollTopButton />
    </>
  );
};

const App = () => {
  console.warn("App: rendu");
  const dispatch = useDispatch();
  const { initialize } = useAuth();

  useEffect(() => {
    initialize();
    dispatch(initializeLanguage());
  }, [dispatch, initialize]);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <HelmetProvider>
          <BrowserRouter>
            <ThemeProvider theme={theme}>
              <ErrorBoundary>
                <AppContent />
                <SocialBar />
                {/* Global CRXCR2 Cams Widget visible on all pages */}
                <Crxcr2CamsWidget 
                  lang="en" 
                  mode="prerecorded" 
                  outlinkUrl="https://t.vlmai-3.com/408074/10339/0?aff_sub5=SF_006OG000004lmDN&aff_sub4=AT_0018" 
                />
              </ErrorBoundary>
            </ThemeProvider>
          </BrowserRouter>
        </HelmetProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
};

export default App;
